/**
 * PDFRenderer component using react-pdf
 * Implements zoom, pan controls, and multi-page navigation
 * Requirements: 1.1, 1.2, 1.4, 1.5
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// Removed unused imports - controls are now in PDFViewerPanel
import { useHighlightingContext } from '@/contexts/HighlightingContext';
import { PDFPageViewport } from '@/types/context';
import { PDFErrorHandler, usePDFErrorHandler } from '../error';

// Configure PDF.js worker — use local copy via Vite to avoid CDN dependency
// (unpkg.com is blocked by corporate VPNs / firewalls, causing PDF load failures)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

interface PDFRendererProps {
  documentUrl: string;
  onLoadSuccess?: (numPages: number) => void;
  onLoadError?: (error: Error) => void;
  className?: string;
}

const PDFRenderer: React.FC<PDFRendererProps> = ({
  documentUrl,
  onLoadSuccess,
  onLoadError,
  className
}) => {
  const { state, actions } = useHighlightingContext();
  const [numPages, setNumPages] = useState<number>(0);
  const [, setLoading] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  
  // Use the PDF error handler hook
  const { 
    error, 
    handleError, 
    retry, 
    reset, 
    reportIssue, 
    canRetry 
  } = usePDFErrorHandler();

  // Track container width via ResizeObserver — debounced to prevent flicker during resize.
  // Edge re-paints intermediate layout states that Chrome/Firefox batch, causing visible
  // flicker when width changes trigger Page re-renders mid-resize.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let rafId: number | null = null;

    const observer = new ResizeObserver((entries) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const entry = entries[0];
        if (entry) {
          const newWidth = Math.floor(entry.contentRect.width) - 20; // 20px padding
          setContainerWidth((prev) => {
            // Only update if width changed by more than 1px — prevents sub-pixel jitter
            if (Math.abs(prev - newWidth) > 1) return newWidth;
            return prev;
          });
        }
      });
    });

    observer.observe(el);
    // Set initial width
    setContainerWidth(Math.floor(el.clientWidth) - 20);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Zoom levels managed by PDFViewerPanel

  const handleDocumentLoadSuccess = useCallback(({ numPages: totalPages }: { numPages: number }) => {
    setNumPages(totalPages);
    setLoading(false);
    reset(); // Clear any previous errors
    onLoadSuccess?.(totalPages);
    console.log('PDF loaded with', totalPages, 'pages'); // Use numPages state
  }, [onLoadSuccess, reset, numPages]);

  const handleDocumentLoadError = useCallback((error: Error) => {
    setLoading(false);
    handleError(error);
    onLoadError?.(error);
  }, [onLoadError, handleError]);

  const handlePageLoadSuccess = useCallback((page: any) => {
    try {
      // Calculate scale to fit width using the stable containerWidth state
      const width = containerRef.current?.clientWidth || 800;
      const pageViewport = page.getViewport({ scale: 1.0 });
      const scale = (width - 20) / pageViewport.width;
      
      const viewport = page.getViewport({ scale });
      actions.updateViewport(viewport as PDFPageViewport, state.currentPage);
    } catch (error) {
      console.error('Failed to get page viewport:', error);
      handleError(error instanceof Error ? error : new Error('Failed to get page viewport'));
    }
  }, [state.currentPage, actions, handleError]);

  // Removed unused handlers - controls are now in PDFViewerPanel

  // Container width management removed - handled by PDFViewerPanel

  // Handle retry functionality
  const handleRetry = useCallback(() => {
    if (retry()) {
      setLoading(true);
      // Force re-render of Document component by updating key
      setNumPages(0);
    }
  }, [retry]);

  if (error) {
    return (
      <Box className={className} sx={{ height: '100%' }}>
        <PDFErrorHandler
          error={error}
          documentUrl={documentUrl}
          onRetry={canRetry ? handleRetry : undefined}
          onReportIssue={reportIssue}
        />
      </Box>
    );
  }

  return (
    <Box className={className} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation and Zoom Controls */}
      {/* Duplicate toolbar removed - controls are in PDFViewerPanel */}

      {/* PDF Document Container */}
      <Box 
        ref={containerRef}
        id="pdf-container"
        sx={{ 
          flex: 1, 
          position: 'relative',
          backgroundColor: '#f5f5f5',
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}
      >
        <Document
          file={documentUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          loading={<CircularProgress size={40} />}
        >
          <Page
            pageNumber={state.currentPage}
            width={containerWidth > 0 ? containerWidth : 800}
            onLoadSuccess={handlePageLoadSuccess}
            loading={<CircularProgress size={24} />}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </Box>
    </Box>
  );
};

export default PDFRenderer;